import { Badge, Button, Divider, Flex } from 'antd';
import { AppstoreOutlined, BlockOutlined, BorderOutlined, CloseOutlined, MinusOutlined } from '@ant-design/icons';

export interface TaskbarItem {
  key: string;
  label: string;
  /** Есть ли в этом свёрнутом окне несохранённые изменения — точка на плашке, не разворачивая. */
  dirty: boolean;
}

/** Выше любого плавающего окна (их z-index растёт по счётчику от 1000 при каждом фокусе). */
const TASKBAR_Z_INDEX = 100000;

/**
 * Панель управления окнами слева внизу (как панель задач в Windows).
 *
 * «Свернуть все»/«Каскадом»/«Сеткой» относятся к открытым (не свёрнутым) окнам и видны, пока
 * хотя бы одно такое есть. «Закрыть все» и плашки — к свёрнутым: клик по плашке разворачивает
 * окно обратно на то же место и в том же размере (окно не размонтировалось); «Закрыть все»
 * закрывает их все разом, но через обычное закрытие каждого окна (с проверкой несохранённых
 * изменений, см. FloatingWindow) — а не в обход неё.
 */
export function WindowTaskbar({
  items,
  hasOpenWindows,
  onRestore,
  onCloseAll,
  onMinimizeAll,
  onCascadeAll,
  onTileAll,
}: {
  items: TaskbarItem[];
  hasOpenWindows: boolean;
  onRestore: (key: string) => void;
  onCloseAll: () => void;
  onMinimizeAll: () => void;
  onCascadeAll: () => void;
  onTileAll: () => void;
}) {
  if (!hasOpenWindows && items.length === 0) return null;
  return (
    <Flex
      data-testid="window-taskbar"
      align="center"
      gap={4}
      wrap
      justify="flex-start"
      style={{ position: 'fixed', left: 8, bottom: 8, maxWidth: 'calc(100vw - 16px)', zIndex: TASKBAR_Z_INDEX }}
    >
      {hasOpenWindows ? (
        <>
          <Button size="small" icon={<MinusOutlined />} title="Свернуть все окна" aria-label="Свернуть все окна" onClick={onMinimizeAll} style={BUTTON_STYLE}>
            Свернуть все
          </Button>
          <Button size="small" icon={<BlockOutlined />} title="Разложить окна каскадом" aria-label="Разложить окна каскадом" onClick={onCascadeAll} style={BUTTON_STYLE} />
          <Button size="small" icon={<AppstoreOutlined />} title="Разложить окна сеткой" aria-label="Разложить окна сеткой" onClick={onTileAll} style={BUTTON_STYLE} />
        </>
      ) : null}

      {hasOpenWindows && items.length > 0 ? <Divider type="vertical" style={{ margin: 0 }} /> : null}

      {items.length > 0 ? (
        <>
          <Button size="small" icon={<CloseOutlined />} title="Закрыть все свёрнутые окна" aria-label="Закрыть все свёрнутые окна" onClick={onCloseAll} style={BUTTON_STYLE}>
            Закрыть все
          </Button>
          <Divider type="vertical" style={{ margin: 0 }} />
          {items.map(({ key, label, dirty }) => (
            <Button key={key} size="small" icon={<BorderOutlined />} title={`Развернуть: ${label}${dirty ? ' (есть несохранённые изменения)' : ''}`} onClick={() => onRestore(key)} style={{ ...BUTTON_STYLE, maxWidth: 240 }}>
              <Badge dot={dirty} offset={[2, 0]}>
                <span style={{ display: 'inline-block', maxWidth: 190, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', verticalAlign: 'bottom' }}>
                  {label}
                </span>
              </Badge>
            </Button>
          ))}
        </>
      ) : null}
    </Flex>
  );
}

const BUTTON_STYLE: React.CSSProperties = { fontSize: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.25)' };
